// Modal "Créditos de Leads" da tela /companies (super admin).
// Espelha o SmsCreditosModal (mesmo visual e estrutura).
//
// Só adição: `POST /leads/admin/creditar` recusa quantidade <= 0
// (creditarLeads) e `debitarLeads` é o débito de busca real — usá-lo para
// estorno administrativo misturaria as duas coisas no extrato. Remover
// crédito precisa de rota própria.
//
// "Motivo" é obrigatório aqui (o backend aceitaria vazio e gravaria um texto
// padrão); exigir no modal mantém o extrato rastreável.
//
// Além do saldo mostra o "reservado" (leads de buscas em andamento): o que o
// cliente realmente pode usar é saldo - reservado.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import TextField from "@material-ui/core/TextField";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import { makeStyles, alpha } from "@material-ui/core/styles";

import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";
import { formatBRL, novoSaldoSms, parseAmount } from "../../pages/Companies/companyRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    headerIcon: {
      width: 38,
      height: 38,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      backgroundColor: alpha(AEROS.cyan, 0.14),
      color: stateText(theme, AEROS.cyan, "info"),
      "& .MuiSvgIcon-root": { fontSize: 20 },
    },
    packGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(4, minmax(0,1fr))",
      gap: 8,
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "repeat(2, minmax(0,1fr))",
      },
    },
    pack: {
      border: `1px solid ${border}`,
      backgroundColor: theme.palette.aeros.surface,
      borderRadius: 10,
      padding: "10px 8px",
      textAlign: "center",
      cursor: "pointer",
      font: "inherit",
      color: "inherit",
      minWidth: 0,
      minHeight: 44,
    },
    packActive: {
      borderColor: AEROS.cyan,
      backgroundColor: alpha(AEROS.cyan, 0.1),
    },
    packQty: {
      fontSize: 15,
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    packPrice: { fontSize: 10.5, color: theme.palette.text.secondary },
    resultRow: {
      display: "flex",
      alignItems: "center",
      fontSize: 12,
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.aeros.surface,
      borderRadius: 9,
      padding: "9px 11px",
    },
    resultValue: {
      marginLeft: "auto",
      fontWeight: 700,
      fontVariantNumeric: "tabular-nums",
      color: stateText(theme, AEROS.success, "success"),
    },
    extratoTitle: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      marginBottom: 8,
    },
    extratoBox: {
      border: `1px solid ${border}`,
      borderRadius: 10,
      maxHeight: 150,
      overflowY: "auto",
      ...theme.scrollbarStyles,
    },
    // Campos de texto do modal: 16px no celular (abaixo disso o iOS dá zoom
    // ao focar). Local ao modal; o estilo compartilhado não foi alterado.
    fieldInput: {
      [theme.breakpoints.down("xs")]: {
        "& .MuiOutlinedInput-root": { fontSize: 16 },
        "& .MuiOutlinedInput-input": { minHeight: 24 },
      },
    },
    extratoRow: {
      display: "grid",
      gridTemplateColumns: "120px 70px 60px minmax(0,1fr)",
      gap: 8,
      padding: "7px 11px",
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      borderBottom: `1px solid ${border}`,
      alignItems: "center",
      "&:last-child": { borderBottom: "none" },
      [theme.breakpoints.down("xs")]: {
        gridTemplateColumns: "minmax(0,1fr) auto",
        rowGap: 2,
        "& > :nth-child(3)": { gridColumn: 2, gridRow: 1 },
        "& > :nth-child(4)": { gridColumn: "1 / -1" },
      },
    },
    extratoQty: { fontVariantNumeric: "tabular-nums", textAlign: "right" },
    extratoReason: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      minWidth: 0,
    },
    extratoEmpty: {
      padding: "12px 11px",
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
      textAlign: "center",
    },
  };
});

const LeadsCreditosModal = ({ open, onClose, company }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const { datetimeToClient } = useDate();

  // A listagem de empresas e a de saldos de leads usam o mesmo `id`.
  const companyId = company?.id;

  const [saldo, setSaldo] = useState(null); // null = indisponível
  const [reservado, setReservado] = useState(null);
  const [pacotes, setPacotes] = useState([]);
  const [extrato, setExtrato] = useState([]);
  const [quantidade, setQuantidade] = useState("");
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);

  const carregar = useCallback(async () => {
    if (!companyId) return;
    try {
      const [saldosResp, pacotesResp, extratoResp] = await Promise.all([
        api.get("/leads/admin/saldos").catch((err) => {
          toastError(err);
          return null;
        }),
        api.get("/leads/admin/pacotes").catch(() => ({ data: [] })),
        api.get(`/leads/admin/extrato/${companyId}`).catch(() => ({ data: [] })),
      ]);
      const atual = saldosResp
        ? (saldosResp.data || []).find((s) => s.id === companyId)
        : null;
      // Falha na consulta: saldo desconhecido, nunca finge 0.
      setSaldo(atual ? atual.leadsSaldo : null);
      setReservado(atual ? atual.leadsReservado : null);
      setPacotes(
        Array.isArray(pacotesResp.data)
          ? pacotesResp.data.filter((p) => p.ativo)
          : []
      );
      setExtrato(Array.isArray(extratoResp.data) ? extratoResp.data : []);
    } catch (err) {
      toastError(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (!open) return;
    setQuantidade("");
    setMotivo("");
    carregar();
  }, [open, carregar]);

  const novoSaldo = useMemo(
    () => (saldo === null ? null : novoSaldoSms(saldo, quantidade, "add")),
    [saldo, quantidade]
  );

  const handleClose = () => {
    setQuantidade("");
    setMotivo("");
    onClose();
  };

  const handleCreditar = async () => {
    const qtd = Number(quantidade);
    if (!Number.isInteger(qtd) || qtd <= 0) {
      toast.error(i18n.t("aeros.companies.leads.invalidAmount"));
      return;
    }
    if (!motivo.trim()) {
      toast.error(i18n.t("aeros.companies.leads.reasonRequired"));
      return;
    }
    setSaving(true);
    try {
      await api.post("/leads/admin/creditar", {
        companyId,
        quantidade: qtd,
        motivo: motivo.trim(),
      });
      toast.success(i18n.t("aeros.companies.leads.applied"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
    setSaving(false);
  };

  const renderTipo = (tipo) =>
    i18n.t(
      String(tipo).toLowerCase() === "credito"
        ? "aeros.companies.leads.credit"
        : "aeros.companies.leads.debit"
    );

  return (
    <AerosDialog
      open={open}
      onClose={handleClose}
      width={520}
      title={`${i18n.t("aeros.companies.leads.title")}${
        company?.name ? ` · ${company.name}` : ""
      }`}
      subtitle={
        saldo === null
          ? i18n.t("aeros.companies.leads.balanceUnavailable")
          : i18n.t("aeros.companies.leads.currentBalance", {
              count: saldo,
              reserved: reservado ?? 0,
            })
      }
      onSubmit={handleCreditar}
      submitLabel={i18n.t("aeros.companies.leads.apply")}
      loading={saving}
      headerIcon={
        <div className={classes.headerIcon}>
          <PeopleAltIcon />
        </div>
      }
    >
      {pacotes.length > 0 && (
        <div className={classes.packGrid}>
          {pacotes.map((pacote) => (
            <button
              type="button"
              key={pacote.id}
              className={`${classes.pack} ${
                Number(quantidade) === pacote.quantidade ? classes.packActive : ""
              }`}
              onClick={() => setQuantidade(String(pacote.quantidade))}
              aria-pressed={Number(quantidade) === pacote.quantidade}
            >
              <div className={classes.packQty}>
                {pacote.quantidade.toLocaleString("pt-BR")}
              </div>
              <div className={classes.packPrice}>
                {formatBRL(parseAmount(pacote.valor), 0)}
              </div>
            </button>
          ))}
        </div>
      )}

      <div className={m.grid2}>
        <div>
          <label className={m.fieldLabel}>
            {i18n.t("aeros.companies.leads.customAmount")}
          </label>
          <TextField
            fullWidth
            type="number"
            variant="outlined"
            className={`${m.input} ${m.inputSmall} ${classes.fieldInput}`}
            placeholder={i18n.t("aeros.companies.leads.amountPlaceholder")}
            value={quantidade}
            onChange={(e) => setQuantidade(e.target.value)}
            inputProps={{ min: 1 }}
          />
        </div>
        <div>
          <label className={m.fieldLabel}>
            {i18n.t("aeros.companies.leads.reason")} *
          </label>
          <TextField
            fullWidth
            variant="outlined"
            className={`${m.input} ${m.inputSmall} ${classes.fieldInput}`}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </div>
      </div>

      <div className={classes.resultRow}>
        {i18n.t("aeros.companies.leads.newBalance")}
        <span className={classes.resultValue}>
          {novoSaldo === null
            ? "—"
            : i18n.t("aeros.companies.leads.balanceUnit", { count: novoSaldo })}
        </span>
      </div>

      <div>
        <div className={classes.extratoTitle}>
          {i18n.t("aeros.companies.leads.statement")}
        </div>
        <div className={classes.extratoBox}>
          {extrato.length === 0 ? (
            <div className={classes.extratoEmpty}>
              {i18n.t("aeros.companies.leads.noEntries")}
            </div>
          ) : (
            extrato.map((lancamento) => (
              <div className={classes.extratoRow} key={lancamento.id}>
                <span>{datetimeToClient(lancamento.createdAt)}</span>
                <span>{renderTipo(lancamento.tipo)}</span>
                <span className={classes.extratoQty}>{lancamento.quantidade}</span>
                <span className={classes.extratoReason} title={lancamento.motivo}>
                  {lancamento.motivo}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </AerosDialog>
  );
};

export default LeadsCreditosModal;
